import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Lock, User, Save, TrendingUp } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingRate, setLoadingRate] = useState(false);
  const [commissionRate, setCommissionRate] = useState(10);
  const [credentials, setCredentials] = useState({
    currentPassword: '',
    newUsername: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchCommissionRate();
  }, []);

  const fetchCommissionRate = async () => {
    try {
      const response = await axios.get(`${API}/admin/commission-rate`);
      setCommissionRate(response.data.commissionRate);
    } catch (error) {
      console.error('Erreur lors du chargement du taux de commission:', error);
    }
  };

  const handleUpdateCommissionRate = async () => {
    if (commissionRate < 0 || commissionRate > 100) {
      toast.error('Le taux doit être entre 0 et 100%');
      return;
    }

    setLoadingRate(true);
    try {
      await axios.put(`${API}/admin/commission-rate`, {
        commissionRate: parseFloat(commissionRate)
      });
      toast.success(`Taux de commission mis à jour: ${commissionRate}%`);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du taux');
    } finally {
      setLoadingRate(false);
    }
  };

  const handleUpdateCredentials = async (e) => {
    e.preventDefault();

    // Validation
    if (credentials.newPassword !== credentials.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (credentials.newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    if (credentials.newUsername.length < 3) {
      toast.error("Le nom d'utilisateur doit contenir au moins 3 caractères");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/update-credentials`, {
        currentPassword: credentials.currentPassword,
        newUsername: credentials.newUsername,
        newPassword: credentials.newPassword
      });

      if (response.data.success) {
        toast.success('Identifiants mis à jour avec succès! Veuillez vous reconnecter.');
        localStorage.removeItem('adminUser');
        setTimeout(() => {
          navigate('/admin/login');
        }, 2000);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Mot de passe actuel incorrect');
      } else {
        toast.error('Erreur lors de la mise à jour des identifiants');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black p-6">
      <div className="max-w-2xl mx-auto">
        <Button
          onClick={() => navigate('/admin/dashboard')}
          variant="ghost"
          className="mb-6 text-gray-300 hover:text-gold"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour au tableau de bord
        </Button>

        <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-gold/30">
          <CardHeader>
            <CardTitle className="text-3xl text-white">Paramètres Administrateur</CardTitle>
            <CardDescription className="text-gray-400 text-lg">
              Modifiez vos identifiants de connexion
            </CardDescription>
          </CardHeader>

          <CardContent>
            {/* Commission Rate Section */}
            <div className="mb-8 pb-8 border-b border-gold/20">
              <h3 className="text-2xl text-gold mb-4 flex items-center gap-2">
                <TrendingUp className="w-6 h-6" />
                Taux de Commission
              </h3>
              <p className="text-gray-400 mb-4">
                Définissez le pourcentage de commission pour les chauffeurs
              </p>
              <div className="flex gap-4 items-end">
                <div className="flex-1 space-y-2">
                  <Label htmlFor="commissionRate" className="text-white text-base">
                    Taux de commission global (%)
                  </Label>
                  <Input
                    id="commissionRate"
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={commissionRate}
                    onChange={(e) => setCommissionRate(e.target.value)}
                    className="bg-gray-800 border-gold/30 text-white text-lg"
                  />
                  <p className="text-xs text-gray-400">
                    Ce taux sera appliqué aux nouvelles courses terminées
                  </p>
                </div>
                <Button
                  onClick={handleUpdateCommissionRate}
                  disabled={loadingRate}
                  className="bg-gradient-to-r from-gold to-amber-600 text-black hover:from-amber-600 hover:to-gold"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loadingRate ? 'Mise à jour...' : 'Enregistrer'}
                </Button>
              </div>
            </div>

            {/* Credentials Section */}
            <h3 className="text-2xl text-gold mb-4 flex items-center gap-2">
              <Lock className="w-6 h-6" />
              Identifiants de Connexion
            </h3>
            <form onSubmit={handleUpdateCredentials} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="currentPassword" className="text-white text-base">
                  Mot de passe actuel *
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-5 w-5 text-gold" />
                  <Input
                    id="currentPassword"
                    type="password"
                    placeholder="Votre mot de passe actuel"
                    value={credentials.currentPassword}
                    onChange={(e) => setCredentials({ ...credentials, currentPassword: e.target.value })}
                    className="bg-gray-800 border-gold/30 text-white pl-10"
                    required
                  />
                </div>
              </div>

              <div className="border-t border-gold/20 pt-6">
                <h3 className="text-xl text-gold mb-4">Nouveaux identifiants</h3>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="newUsername" className="text-white text-base">
                      Nouveau nom d'utilisateur *
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-5 w-5 text-gold" />
                      <Input
                        id="newUsername"
                        type="text"
                        placeholder="Minimum 3 caractères"
                        value={credentials.newUsername}
                        onChange={(e) => setCredentials({ ...credentials, newUsername: e.target.value })}
                        className="bg-gray-800 border-gold/30 text-white pl-10"
                        required
                        minLength={3}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="newPassword" className="text-white text-base">
                      Nouveau mot de passe *
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-5 w-5 text-gold" />
                      <Input
                        id="newPassword"
                        type="password"
                        placeholder="Minimum 6 caractères"
                        value={credentials.newPassword}
                        onChange={(e) => setCredentials({ ...credentials, newPassword: e.target.value })}
                        className="bg-gray-800 border-gold/30 text-white pl-10"
                        required
                        minLength={6}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-white text-base">
                      Confirmer le nouveau mot de passe *
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-5 w-5 text-gold" />
                      <Input
                        id="confirmPassword"
                        type="password"
                        placeholder="Confirmez votre mot de passe"
                        value={credentials.confirmPassword}
                        onChange={(e) => setCredentials({ ...credentials, confirmPassword: e.target.value })}
                        className="bg-gray-800 border-gold/30 text-white pl-10"
                        required
                        minLength={6}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                size="lg"
                disabled={loading}
                className="w-full bg-gradient-to-r from-gold to-amber-600 text-black hover:from-amber-600 hover:to-gold"
              >
                <Save className="w-5 h-5 mr-2" />
                {loading ? 'Mise à jour...' : 'Mettre à jour les identifiants'}
              </Button>

              <p className="text-sm text-gray-400 text-center">
                ⚠️ Après la mise à jour, vous devrez vous reconnecter avec vos nouveaux identifiants
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminSettings;
